import { useQuery } from "@tanstack/react-query";

import { api, ApiError } from "./client";
import type { FieldItem, ServiceItem } from "./catalog";

export interface MenuItem {
  id: number;
  name: string;
  image_url: string | null;
  categories_count: number;
}

export interface MenuListResponse {
  menus: MenuItem[];
}

export interface MenuCategoryItem {
  category_id: number;
  category_name: string;
  image_url: string | null;
  services_count: number;
}

export interface MenuDetailResponse {
  id: number;
  name: string;
  image_url: string | null;
  categories: MenuCategoryItem[];
}

export interface CategoryDetailResponse {
  category_id: number;
  category_name: string;
  image_url: string | null;
  menu_id: number | null;
  menu_name: string | null;
  fields: FieldItem[];
  services: ServiceItem[];
}

export function useMenus() {
  return useQuery<MenuListResponse, ApiError>({
    queryKey: ["menus"],
    queryFn: () => api.get<MenuListResponse>("/web/api/menus"),
    staleTime: 60_000,
  });
}

export function useMenuDetail(menuId: number | null) {
  return useQuery<MenuDetailResponse, ApiError>({
    queryKey: ["menu", menuId],
    queryFn: () =>
      api.get<MenuDetailResponse>(`/web/api/menus/${menuId}`),
    enabled: menuId !== null,
    staleTime: 60_000,
  });
}

export function useCategoryDetail(categoryId: number | null) {
  return useQuery<CategoryDetailResponse, ApiError>({
    queryKey: ["category", categoryId],
    queryFn: () =>
      api.get<CategoryDetailResponse>(
        `/web/api/categories/${categoryId}`,
      ),
    enabled: categoryId !== null,
    staleTime: 30_000,
  });
}

/* -------- direct top-up index (Giftery recharge_fixed) -------- */

export interface DirectTopupCard {
  category_id: number;
  name: string;
  image_url: string | null;
  services_count: number;
  /** Optional menu grouping. Null = "Other" section in the index. */
  menu_id: number | null;
  menu_name: string | null;
  menu_image_url: string | null;
}

export interface DirectTopupListResponse {
  categories: DirectTopupCard[];
}

export function useDirectTopupCategories() {
  return useQuery<DirectTopupListResponse, ApiError>({
    queryKey: ["direct_topup", "categories"],
    queryFn: () =>
      api.get<DirectTopupListResponse>(
        "/web/api/direct_topup/categories",
      ),
    staleTime: 60_000,
  });
}

/* -------- direct top-up menu drill-down (menu -> categories) -------- */

export function useDirectTopupMenus() {
  return useQuery<MenuListResponse, ApiError>({
    queryKey: ["direct_topup", "menus"],
    queryFn: () =>
      api.get<MenuListResponse>("/web/api/direct_topup/menus"),
    staleTime: 60_000,
  });
}

export function useDirectTopupMenuDetail(menuId: number | null) {
  return useQuery<MenuDetailResponse, ApiError>({
    queryKey: ["direct_topup", "menu", menuId],
    queryFn: () =>
      api.get<MenuDetailResponse>(
        `/web/api/direct_topup/menus/${menuId}`,
      ),
    enabled: menuId !== null,
    staleTime: 60_000,
  });
}

/* -------- direct top-up full stock (used by the search box) -------- */
//
// Mirrors `useStock()` for the regular catalog: returns the full DT
// catalogue with services nested per category, so the SPA can match
// against category names AND service names without paginating.

import type { StockResponse } from "./catalog";

export function useDirectTopupStock() {
  return useQuery<StockResponse, ApiError>({
    queryKey: ["direct_topup", "stock"],
    queryFn: () =>
      api.get<StockResponse>("/web/api/direct_topup/stock"),
    staleTime: 60_000,
  });
}
